/**
 * Products slice (SCRUM-1954, RTK bundle 5 of 5) — the former switch reducer
 * productReducer.js as createSlice. Behaviour-preserving: every case answers
 * the same legacy action type string (productTypes.js) that productActions.ts
 * already dispatches, and returns the same state. The actions module is
 * unchanged, so the changeset / pending-change contracts the product screens
 * branch on (SCRUM-1838 / SCRUM-1883) stay verbatim and no importer changes.
 * The two cases that change nothing still return a new, equal object, as the
 * switch did. Characterised by
 * tests/integration/reducers/productReducer.characterisation.test.js.
 *
 * What changes: Immer deep-freezes what a slice stores. The three places that
 * wrote into stored product/property objects in place (Label.jsx, LinkedSites.jsx)
 * copy first since this bundle; see comment 24916 on SCRUM-1954.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  GET_ADS,
  ADD_PRODUCT,
  GET_PRODUCTS,
  DELETE_PRODUCT,
  GET_PRODUCTS_BY_COUNTRYCODE,
  ADD_PRODUCT_PROPERTIES,
  UPDATE_PRODUCT_PROPERTIES,
  GET_PRODUCTPROP,
  DELETE_PRODUCT_PROPERTIES,
  GET_PRODUCTS_BY_ID,
  UPDATE_PRODUCT,
  CLEAR_MESSAGE,
  GET_ALL_PROPERTIES,
  ADD_PROPERTY,
  UPDATE_PROPERTY,
  GET_CLIENTS_BY_COUNTRYCODE,
  CLEAR_PRODUCTS_BY_ID,
  LINK_PRODUCT,
  PUSH_CLICKOUT,
  CLEAR_CLICKOUT,
  PATCH_PROPERTY,
  CLEAR_PRODUCTS,
} from "./productTypes";

type ProductsState = {
  products: any[];
  products_country_code: any[];
  clients_country_code: any[];
  productproperties: any;
  product_byId: any;
  message: string;
  /** as loose as the switch reducer left it (consumers default it themselves) */
  properties: any;
  ads: any[];
  /** clickout + image pushed by the listing (PUSH_CLICKOUT) */
  clickout: any;
};

const initialState: ProductsState = {
  products: [],
  products_country_code: [],
  clients_country_code: [],
  productproperties: {},
  product_byId: {},
  message: "",
  properties: [],
  ads: [],
  clickout: {},
};

/** The server message a write answers with. */
const storeMessage = <T extends string>(state: ProductsState, action: PayloadAction<{ msg: string }, T>) => ({ ...state, message: action.payload.msg });

/** A case that changes nothing but, like the switch, returns a new object. */
const copy = (state: ProductsState) => ({ ...state });

const replace = <T extends string>(key: keyof ProductsState) => (state: ProductsState, action: PayloadAction<unknown, T>) => ({ ...state, [key]: action.payload });

const productSlice = createSlice({
  name: "products",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_MESSAGE, (state) => ({ ...state, message: "" }))
      .addCase(PUSH_CLICKOUT, replace<typeof PUSH_CLICKOUT>("clickout"))
      .addCase(CLEAR_CLICKOUT, (state) => ({ ...state, clickout: {} }))
      .addCase(ADD_PRODUCT, storeMessage<typeof ADD_PRODUCT>)
      .addCase(UPDATE_PRODUCT, storeMessage<typeof UPDATE_PRODUCT>)
      .addCase(LINK_PRODUCT, storeMessage<typeof LINK_PRODUCT>)
      .addCase(ADD_PRODUCT_PROPERTIES, replace<typeof ADD_PRODUCT_PROPERTIES>("productproperties"))
      .addCase(GET_PRODUCTS, replace<typeof GET_PRODUCTS>("products"))
      .addCase(CLEAR_PRODUCTS, (state) => ({ ...state, products: [] }))
      .addCase(GET_ADS, replace<typeof GET_ADS>("ads"))
      .addCase(GET_PRODUCTS_BY_COUNTRYCODE, replace<typeof GET_PRODUCTS_BY_COUNTRYCODE>("products_country_code"))
      .addCase(GET_CLIENTS_BY_COUNTRYCODE, replace<typeof GET_CLIENTS_BY_COUNTRYCODE>("clients_country_code"))
      .addCase(UPDATE_PRODUCT_PROPERTIES, storeMessage<typeof UPDATE_PRODUCT_PROPERTIES>)
      .addCase(GET_PRODUCTPROP, (state, action: PayloadAction<{ properties?: any }, typeof GET_PRODUCTPROP>) => ({
        ...state,
        productproperties: action.payload,
        properties: action.payload.properties,
      }))
      .addCase(DELETE_PRODUCT, copy)
      .addCase(DELETE_PRODUCT_PROPERTIES, (state) => ({ ...state, productproperties: null }))
      .addCase(GET_PRODUCTS_BY_ID, replace<typeof GET_PRODUCTS_BY_ID>("product_byId"))
      .addCase(CLEAR_PRODUCTS_BY_ID, (state) => ({ ...state, product_byId: {} }))
      .addCase(GET_ALL_PROPERTIES, replace<typeof GET_ALL_PROPERTIES>("properties"))
      .addCase(ADD_PROPERTY, storeMessage<typeof ADD_PROPERTY>)
      .addCase(UPDATE_PROPERTY, storeMessage<typeof UPDATE_PROPERTY>)
      .addCase(PATCH_PROPERTY, copy);
  },
});

export default productSlice.reducer;
